/**
 * Entity-name vocabulary and category inference: the single frontend mirror of
 * backend `analysis/entity_constants.py` (repo rules R14 / R17, same discipline
 * as the R29 identifier canon).
 *
 * The data is NOT typed in here. It is read from `entityKeywords.snapshot.json`,
 * which `backend/scripts/dump_entity_keywords.py` writes from the backend module
 * and `backend/tests/test_entity_keywords_parity.py` locks (it fails when the
 * backend changes without the snapshot being regenerated). The matching rules
 * below mirror `entity_classification.keyword_in_name`,
 * `entity_constants.infer_category_from_name`, `is_exchange_by_name` and
 * `is_compliant_exchange`; `entityKeywords.test.ts` runs the shared
 * `entityKeywords.cases.json` table through them.
 *
 * Never add an inline exchange / mixer keyword list anywhere else in the app.
 */
import snapshot from './entityKeywords.snapshot.json';

interface CategoryRule {
  category: string;
  keywords: string[];
}

export const SHORT_KEYWORD_MAX_LEN: number = snapshot.short_keyword_max_len;
export const AMBIGUOUS_WORD_KEYWORDS: ReadonlySet<string> = new Set(snapshot.ambiguous_word_keywords);
export const UNINFORMATIVE_ENTITY_TYPES: ReadonlySet<string> = new Set(snapshot.uninformative_types);
export const CARD_OFFRAMP_CATEGORY: string = snapshot.card_offramp_category;
export const CARD_OFFRAMP_KEYWORDS: readonly string[] = snapshot.card_offramp_keywords;
export const EXCHANGE_BRAND_KEYWORDS: readonly string[] = snapshot.exchange_brand_keywords;
export const EXCHANGE_ROLE_KEYWORDS: readonly string[] = snapshot.exchange_role_keywords;
export const EXCHANGE_NAME_KEYWORDS: readonly string[] = [
  ...EXCHANGE_BRAND_KEYWORDS,
  ...EXCHANGE_ROLE_KEYWORDS,
];
export const COMPLIANT_INDIAN_EXCHANGES: readonly string[] = snapshot.compliant_indian_exchanges;
/** Category resolution order: the first category with a matching keyword wins. */
export const CATEGORY_INFERENCE: readonly CategoryRule[] = snapshot.category_inference;

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const boundaryCache = new Map<string, RegExp>();
function boundaryPattern(keyword: string): RegExp {
  let re = boundaryCache.get(keyword);
  if (!re) {
    re = new RegExp('(?<![a-z])' + escapeRegExp(keyword) + '(?![a-z])');
    boundaryCache.set(keyword, re);
  }
  return re;
}

/** One keyword against a lower-cased name (mirror of entity_classification.keyword_in_name). */
export function keywordInName(keyword: string, nameLower: string, forceBoundary = false): boolean {
  if (forceBoundary || keyword.length <= SHORT_KEYWORD_MAX_LEN || AMBIGUOUS_WORD_KEYWORDS.has(keyword)) {
    return boundaryPattern(keyword).test(nameLower);
  }
  return nameLower.includes(keyword);
}

/** Category a name implies, or null. Mirror of entity_constants.infer_category_from_name. */
export function inferCategoryFromName(entityName: string | null | undefined): string | null {
  if (!entityName) return null;
  const nameLower = entityName.toLowerCase();
  // Card off-ramp issuers first, word-boundary only (they are not exchanges).
  if (CARD_OFFRAMP_KEYWORDS.some(kw => keywordInName(kw, nameLower, true))) {
    return CARD_OFFRAMP_CATEGORY;
  }
  for (const rule of CATEGORY_INFERENCE) {
    if (rule.keywords.some(kw => keywordInName(kw, nameLower))) return rule.category;
  }
  return null;
}

/** Mirror of entity_constants.is_compliant_exchange (FIU-IND registered Indian exchanges). */
export function isCompliantExchange(entityName: string | null | undefined): boolean {
  if (!entityName) return false;
  const nameLower = entityName.toLowerCase();
  const nameNorm = nameLower.replace(/[\s._-]+/g, '');
  const sep = /[\s._-]+/;
  const boundaryTokens = new Set<string>(snapshot.compliant_boundary_tokens);
  return COMPLIANT_INDIAN_EXCHANGES.some(tok => {
    const compactLen = tok.replace(/[\s._-]+/g, '').length;
    if (compactLen <= snapshot.compliant_boundary_max_len || boundaryTokens.has(tok)) {
      const parts = tok.split(sep).filter(Boolean).map(escapeRegExp);
      return new RegExp('(?<![a-z])' + parts.join('[\\s._-]*') + '(?![a-z])').test(nameLower);
    }
    return nameNorm.includes(tok);
  });
}

/**
 * True when the name marks an exchange. Mirror of entity_constants.is_exchange_by_name:
 * a mixer or no-KYC swap service whose name carries a role word ("... Deposit")
 * is not an exchange; an FIU-registered exchange that the no-KYC list also names
 * (CoinSwitch) stays one.
 */
export function isExchangeByName(entityName: string | null | undefined): boolean {
  const category = inferCategoryFromName(entityName);
  if (category === 'exchange') return true;
  return category === 'no_kyc_exchange' && isCompliantExchange(entityName);
}

/**
 * Canonical type resolution (mirror of entity_constants.resolve_entity_type): an
 * informative type is returned lower-cased; an uninformative one ("labeled",
 * "other", "unknown", "") is inferred from the name.
 */
export function resolveEntityType(
  entityType: string | null | undefined,
  entityName: string | null | undefined,
): string | null {
  if (entityType && !UNINFORMATIVE_ENTITY_TYPES.has(entityType.toLowerCase())) {
    return entityType.toLowerCase();
  }
  const inferred = inferCategoryFromName(entityName);
  if (inferred) return inferred;
  return entityType ? entityType.toLowerCase() : null;
}
